import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, CandlestickChart, Activity, Newspaper, Layers, ShieldAlert } from "lucide-react";

const FACTORS = [
  { key: "chart_pattern", label: "Chart Pattern", icon: CandlestickChart },
  { key: "technical_indicators", label: "Technical Indicators", icon: Activity },
  { key: "news_sentiment", label: "News Sentiment", icon: Newspaper },
  { key: "support_resistance", label: "Support / Resistance", icon: Layers },
  { key: "risk_factors", label: "Risk Factors", icon: ShieldAlert },
];

const IMPACT = {
  bullish: { text: "text-emerald-400", bar: "#10B981", label: "Bullish" },
  bearish: { text: "text-red-400", bar: "#EF4444", label: "Bearish" },
  neutral: { text: "text-amber-400", bar: "#F59E0B", label: "Neutral" },
};

const fmt = (n) => (n === null || n === undefined ? null : typeof n === "number" ? n.toLocaleString("en", { maximumFractionDigits: 2 }) : n);

function chips(key, f) {
  const c = [];
  if (key === "chart_pattern") { if (f.name) c.push(f.name); if (f.historical_win_rate != null) c.push(f.sample_size ? `${f.historical_win_rate}% backtested over ${f.sample_size} occurrences` : `${f.historical_win_rate}% win historically`); }
  if (key === "technical_indicators") { if (f.rsi != null) c.push(`RSI ${fmt(f.rsi)}`); if (f.macd) c.push(`MACD ${String(f.macd).replace(/^MACD\s*/i, "")}`); if (f.volume) c.push(`Vol ${f.volume}`); }
  if (key === "news_sentiment") { if (f.sentiment_score != null) c.push(`${f.sentiment_score}% positive`); if (f.summary) c.push(f.summary); }
  if (key === "support_resistance") { if (f.support != null) c.push(`Support ${fmt(f.support)}`); if (f.resistance != null) c.push(`Resistance ${fmt(f.resistance)}`); }
  if (key === "risk_factors") (f.factors || []).forEach((x) => c.push(x));
  return c;
}

export const SignalReasoning = ({ reasoning, recommendation, probability, testId = "signal-reasoning", defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!reasoning) return null;
  const sorted = FACTORS.map((f) => ({ ...f, data: reasoning[f.key] })).filter((f) => f.data);

  return (
    <div className="border-t border-white/5 pt-3" data-testid={testId}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`} aria-expanded={open}
        className="w-full flex items-center justify-between text-sm font-semibold text-slate-200 hover:text-emerald-400 transition-colors group">
        <span>{open ? "Hide Analysis" : "View Analysis"} <span className="text-slate-500 font-normal">· Why this signal?</span></span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden" data-testid={`${testId}-content`}>
            <p className="text-xs text-slate-500 mt-3 mb-4">
              How each factor contributed to the <span className={recommendation === "BUY" ? "text-emerald-400" : recommendation === "SELL" ? "text-red-400" : "text-amber-400"}>{probability}% {recommendation}</span> score.
            </p>

            <div className="flex h-2 rounded-full overflow-hidden bg-white/5 mb-5" data-testid={`${testId}-stacked-bar`} aria-hidden>
              {sorted.map((f) => (
                <div key={f.key} title={`${f.label} ${f.data.contribution_pct}%`} style={{ width: `${f.data.contribution_pct}%`, background: IMPACT[f.data.impact]?.bar || IMPACT.neutral.bar }} className="h-full border-r border-[#0D111A] last:border-0" />
              ))}
            </div>

            <ul className="space-y-4">
              {sorted.map((f, i) => {
                const imp = IMPACT[f.data.impact] || IMPACT.neutral;
                return (
                  <motion.li key={f.key} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} data-testid={`${testId}-factor-${f.key}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <f.icon className={`w-4 h-4 shrink-0 ${imp.text}`} />
                        <span className="text-sm font-medium truncate">{f.label}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 ${imp.text}`}>{imp.label}</span>
                      </div>
                      <span className="font-mono-num text-sm font-bold shrink-0" data-testid={`${testId}-contribution-${f.key}`}>{f.data.contribution_pct}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-white/5 mb-1.5 overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${f.data.contribution_pct}%` }} transition={{ duration: 0.6, delay: 0.1 + i * 0.05 }} className="h-full rounded-full" style={{ background: imp.bar }} />
                    </div>
                    <p className="text-sm text-slate-300 leading-snug">{f.data.detail}</p>
                    {chips(f.key, f.data).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {chips(f.key, f.data).map((c, j) => <span key={j} title={c} className="text-[11px] font-mono-num bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-slate-300 max-w-[280px] truncate">{c}</span>)}
                      </div>
                    )}
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
